import {Button} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback} from 'react';

import {ChartAggOption} from '../../../generated/graphql';
import {useInheritedSettings} from '../../Panel/hooks/useInheritedSettings';
import {usePanelContext} from '../../Panel/PanelContextProvider';
import {ResetSettingButton} from '../../WorkspaceDrawer/Settings/components/ResetSettingButton';
import {SettingIndentedRow} from '../../WorkspaceDrawer/Settings/components/SettingIndentedRow';
import {SettingLabel} from '../../WorkspaceDrawer/Settings/components/SettingLabel';
import {SettingToggleRow} from '../../WorkspaceDrawer/Settings/components/SettingToggleRow';
import {type useTrackSettingsChange} from '../../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
import {DEFAULT_LINE_PLOT_SETTINGS} from '../../WorkspaceDrawer/Settings/runLinePlots/linePlotDefaults';
import {type LinePlotSettings} from '../../WorkspaceDrawer/Settings/types';
import {usePanelConfigContext} from '../PanelConfigContext';
import {type RunsLinePlotConfig} from '../types';

const EMPTY_GROUPING_SETTINGS: Partial<LinePlotSettings> = {
  useRunsTableGroupingInPanels: undefined,
};

// Mirrors the section drawer's aggregation options.
const GROUP_AGGREGATION_OPTIONS: {label: string; value: ChartAggOption}[] = [
  {label: 'Mean', value: ChartAggOption.Mean},
  {label: 'Min', value: ChartAggOption.Min},
  {label: 'Max', value: ChartAggOption.Max},
];

/**
 * Panel-level override for "use the runs table grouping in charts", the same
 * setting the workspace and section drawers expose, plus the aggregation that
 * applies to the resulting groups.
 *
 * Aggregation is a single `groupAgg` shared with the Grouping tab's own "Agg"
 * control -- a panel only ever renders one grouping, so it only ever has one
 * aggregation. Giving this control its own key would let it shadow the
 * Grouping tab's, leaving that one looking broken.
 */
export const EditRunsTableGrouping = ({
  config,
  setRunOnApply,
  trackPanelSettingsChange,
  updateConfig,
}: {
  config: RunsLinePlotConfig;
  setRunOnApply: (runOnApply: () => void) => void;
  trackPanelSettingsChange: ReturnType<typeof useTrackSettingsChange>;
  updateConfig: (config: RunsLinePlotConfig) => void;
}) => {
  const {useRunsTableGroupingInPanels} = usePanelConfigContext();
  const inheritedSettings = useInheritedSettings();

  // `config` here is already cascaded, so it can't tell an override apart from
  // an inherited value that happens to match. Read the panel's stored config
  // directly instead -- that's what the workspace and section drawers pass as
  // their own `value`, and it's what decides whether a reset applies.
  const {panel} = usePanelContext();
  const ownConfig = panel?.config as RunsLinePlotConfig | undefined;

  // "Use only latest run per group" leaves a single run per group, so there is
  // nothing to aggregate. Same guard the section drawer applies.
  const isAggregationDisabled = Number.isFinite(
    inheritedSettings?.linePlot?.groupingDepth
  );

  const ownValue = ownConfig?.useRunsTableGroupingInPanels;

  // Nothing set anywhere still renders as Mean, so fall back to the default
  // rather than leaving every button unhighlighted. Same as AggType does.
  const displayGroupAgg =
    config.groupAgg ?? DEFAULT_LINE_PLOT_SETTINGS.groupAgg;
  const ownGroupAgg = ownConfig?.groupAgg;

  const handleCheckedChange = useCallback(
    (newValue: boolean) => {
      // The panel editor applies on Apply, so defer the event to match.
      setRunOnApply(() => () => {
        trackPanelSettingsChange({
          setting: 'useRunsTableGroupingInPanels',
          settingValue: newValue.toString(),
        });
      });
      updateConfig({useRunsTableGroupingInPanels: newValue});
    },
    [setRunOnApply, trackPanelSettingsChange, updateConfig]
  );

  const handleClearSetting = useCallback(() => {
    // Back to undefined so the workspace/section value cascades again.
    updateConfig({useRunsTableGroupingInPanels: undefined});
  }, [updateConfig]);

  const handleGroupAggChange = useCallback(
    (value: ChartAggOption) => {
      setRunOnApply(() => () => {
        trackPanelSettingsChange({setting: 'groupAgg', settingValue: value});
      });
      updateConfig({groupAgg: value});
    },
    [setRunOnApply, trackPanelSettingsChange, updateConfig]
  );

  const handleClearGroupAgg = useCallback(() => {
    updateConfig({groupAgg: undefined});
  }, [updateConfig]);

  return (
    <Tailwind>
      <div
        className="my-12 flex flex-col gap-8"
        data-test="edit-data-runs-table-grouping">
        <SettingToggleRow
          clearSetting={handleClearSetting}
          displayValue={useRunsTableGroupingInPanels}
          emptySettings={EMPTY_GROUPING_SETTINGS}
          label="Use grouping from the runs table in charts"
          onCheckedChange={handleCheckedChange}
          settingKey="useRunsTableGroupingInPanels"
          settingLevel="panel"
          value={ownValue}
        />
        {useRunsTableGroupingInPanels && (
          <SettingIndentedRow className="gap-8 pl-14">
            <div className="flex items-center justify-between">
              <SettingLabel text="Aggregation" className="w-[87px]" />
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-2">
                  {GROUP_AGGREGATION_OPTIONS.map(option => (
                    <Button
                      key={option.value}
                      active={displayGroupAgg === option.value}
                      disabled={isAggregationDisabled}
                      variant="secondary"
                      onClick={() => handleGroupAggChange(option.value)}>
                      {option.label}
                    </Button>
                  ))}
                </div>
                <ResetSettingButton
                  isEnabled={!isAggregationDisabled && ownGroupAgg != null}
                  settingLevel="panel"
                  onButtonClick={handleClearGroupAgg}
                />
              </div>
            </div>
          </SettingIndentedRow>
        )}
      </div>
    </Tailwind>
  );
};
